// quicklog.js — parse natural set notation typed or spoken mid-workout.
//
//   "185x8"  "185 x 8, 8, 7"  "185 for 8 8 7"  "3x8 @ 185"  "3 sets of 8 at 185"
//   "bench 225 for 5 at rpe 8"  "12 reps bodyweight"  "100kg x 5"  "60 seconds"
//   "pull ups bw+25 x 8"  "assisted dips bw-40 x 10"  "+20 for 6"
//   "5k run 25:30"  "row 2000m 7:45"  "bike 45 min"  "3 miles in 24:10"
//   "plank 60s"  "plank 1:30"  "wall sit 3x45s"
//
// parseQuickLog(text, { units }) → {
//   exercise: string|null   exercise words (e.g. "bench", "run")
//   sets: [{ w: number|null (display units), reps: number, rpe: number|null,
//            dist?: metres, dur?: seconds }]
//   unit: 'kg'|'lb'|null    explicit unit if the user said one
//   kind: 'reps' | 'time' | 'cardio'
//   load?: 'bodyweight' | 'weighted-bw' | 'assisted'   when said ("bw+25", "assisted")
// } or null when nothing usable was found. Cardio sets have reps 0.

const WORDNUM = {
  zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18,
  nineteen: 19, twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90,
};
const DIGIT = '(one|two|three|four|five|six|seven|eight|nine)';
const TEEN_TENS = '(ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)';

// Spoken weights: "one thirty five" = 135, "two twenty" = 220, "one fifteen" = 115,
// "one hundred" = 100, "two hundred and five" = 205.
function spokenHundreds(t) {
  t = t.replace(new RegExp(`\\b${DIGIT} hundred(?: and)?(?: ${TEEN_TENS})?(?: ${DIGIT})?\\b`, 'g'), (m, h, tt, d) =>
    String(WORDNUM[h] * 100 + (tt ? WORDNUM[tt] : 0) + (d ? WORDNUM[d] : 0)));
  t = t.replace(new RegExp(`\\b${DIGIT} ${TEEN_TENS}(?: ${DIGIT})?\\b`, 'g'), (m, h, tt, d) => {
    if (d && WORDNUM[tt] < 20) return m; // "one ten five" isn't a number
    return String(WORDNUM[h] * 100 + WORDNUM[tt] + (d ? WORDNUM[d] : 0));
  });
  return t;
}

function normalize(text) {
  let t = ` ${String(text || '').toLowerCase()} `;
  t = spokenHundreds(t);
  // Comma decimals ("9,5 x 10" in kg locales), only right before x / a unit / "for".
  t = t.replace(/(\d),(\d)\s*(?=x|kg|lb|for\b)/g, '$1.$2 ');
  // Speech-recognition artifacts
  t = t.replace(/(\d)\s*(?:by|times|×|\*)\s*(\d)/g, '$1x$2');
  t = t.replace(/(\d)(kgs?|lbs?)/g, '$1 $2');
  t = t.replace(/\b(\d+)\s*pounds?\b/g, '$1 lb').replace(/\b(\d+)\s*(?:kilos?|kilograms?)\b/g, '$1 kg');
  t = t.replace(/\b(lbs?|pounds?)\b/g, 'lb').replace(/\b(kgs?|kilos?|kilograms?)\b/g, 'kg');
  t = t.replace(/\b(bodyweight|body weight|bw)\b/g, ' bw ');
  t = t.replace(/\b([a-z]+)\b/g, (m) => (m in WORDNUM ? String(WORDNUM[m]) : m));
  t = t.replace(/,/g, ' , ').replace(/\s+/g, ' ');
  return t;
}

// Several clauses ("squat 135x8 then 155x6", "135x8; 155x6", "135 x 8, 95 x 6")
// log every clause; a clause that can't be read is returned in `unparsed`,
// never dropped silently. Each set keeps its own unit ("60kg x 5, 135 x 5").
const PAIR = /\d\s*(?:kg|kgs|lb|lbs|pounds?|kilos?)?\s*(?:x|×|\*|for|by)\s*\d/i;
export function parseQuickLog(text, opts = {}) {
  let parts = String(text || '').split(/\s*(?:;|\band then\b|\bthen\b)\s*/i).map(p => p.trim()).filter(Boolean);
  // Commas separate whole sets only when each piece is its own "weight x reps"
  // ("185 x 8, 8, 7" stays one clause with three rep counts).
  parts = parts.flatMap(p => {
    const bits = p.split(/\s*,\s*/).filter(Boolean);
    if (bits.length < 2 || bits.filter(b => PAIR.test(b)).length < 2) return [p];
    const out = [];
    for (const b of bits) { if (PAIR.test(b) || !out.length) out.push(b); else out[out.length - 1] += ', ' + b; }
    return out;
  });
  if (parts.length < 2) return parseOne(text, opts);
  const first = parseOne(parts[0], opts);
  if (!first) return parseOne(text, opts);
  const sets = [...first.sets];
  const unparsed = [];
  const units = new Set([first.unit || null]);
  for (const p of parts.slice(1)) {
    const r = parseOne(p, opts);
    if (r && r.sets.length && (r.kind || 'reps') === (first.kind || 'reps')) { sets.push(...r.sets); units.add(r.unit || null); }
    else unparsed.push(p);
  }
  return { ...first, sets, unit: units.size === 1 ? first.unit : null, ...(unparsed.length ? { unparsed } : {}) };
}

// ——— Cardio and timed holds ————————————————————————————————————————————
const CARDIO_WORDS = /\b(run|ran|runs|running|jog|jogged|jogging|walk|walked|walking|treadmill|cycle|cycled|cycling|bike|biked|biking|ride|rode|elliptical|stairs?|stairmaster|stepmill|row|rowed|rowing|rower|erg|skierg|swim|swam|swimming|hike|hiked|hiking|sprint|sprints|ruck|rucked|skip|skipping)\b/;
const HOLD_WORDS = /\b(plank|planks|hold|holds|hang|hangs|wall sit|l-sit|l sit|hollow)\b/;
const FILLER = /\b(i|did|do|log|logged|add|a|an|the|in|for|of|at|with|and|just|then|set|sets|x|bw|kg|lb|km|kms|k|mi|mile|miles|m|meters?|metres?|kilomet(?:er|re)s?|min|mins|minutes?|h|hr|hrs|hours?|s|sec|secs|seconds?|rpe|reps?)\b/g;
const wordsOf = (t) => {
  const PAST = { ran: 'run', jogged: 'jog', walked: 'walk', biked: 'bike', rode: 'bike', ride: 'bike', cycled: 'cycle', rowed: 'row', swam: 'swim', hiked: 'hike', rucked: 'ruck' };
  const w = t.replace(/[^a-z\s-]/g, ' ').replace(FILLER, ' ').replace(/\b[a-z]+\b/g, (x) => PAST[x] || x).replace(/\s+/g, ' ').trim();
  return w.length >= 3 ? w : null;
};
const clockSecs = (m) => (m[3] != null ? +m[1] * 3600 + +m[2] * 60 + +m[3] : +m[1] * 60 + +m[2]);

function parseCardio(t, units) {
  let dist = null, dur = null;
  let body = t;
  const clock = body.match(/\b(\d{1,2}):(\d{2})(?::(\d{2}))?\b/);
  if (clock) { dur = clockSecs(clock); body = body.replace(clock[0], ' '); }
  const dm = body.match(/(\d+(?:\.\d+)?)\s*(km|kms|k|kilomet(?:er|re)s?|mi|miles?|meters?|metres?|m)(?![a-z])/);
  if (dm) {
    const v = +dm[1], u = dm[2];
    if (/^(k|km|kms|kilomet)/.test(u)) dist = v * 1000;
    else if (/^mi/.test(u)) dist = v * 1609.344;
    else if (/^met/.test(u) || v >= 100) dist = v;          // "2000m"
    else if (dur == null) dur = Math.round(v * 60);           // "30m" = minutes
    body = body.replace(dm[0], ' ');
  }
  if (dur == null) {
    const tm = body.match(/(\d+(?:\.\d+)?)\s*(hours?|hrs?|h|minutes?|mins?|min)(?![a-z])/);
    if (tm) { dur = Math.round(+tm[1] * (/^h/.test(tm[2]) ? 3600 : 60)); body = body.replace(tm[0], ' '); }
  }
  const cardio = CARDIO_WORDS.test(t);
  if (!(dist > 0 || dur > 0)) return null;
  if (!cardio && !(dist > 0 && dur > 0)) return null;
  if (dist != null && (dist <= 0 || dist > 500000)) return null;
  if (dur != null && (dur <= 0 || dur > 24 * 3600)) return null;
  return { exercise: wordsOf(t), sets: [{ w: null, reps: 0, rpe: null, ...(dist ? { dist: Math.round(dist) } : {}), ...(dur ? { dur } : {}) }], unit: null, units, kind: 'cardio' };
}

function parseHold(t, units, rpe) {
  if (!HOLD_WORDS.test(t) || /\breps?\b/.test(t)) return null;
  const secsOf = (s) => {
    let m;
    if ((m = s.match(/^(\d{1,2}):(\d{2})$/))) return +m[1] * 60 + +m[2];
    if ((m = s.match(/^(\d+(?:\.\d+)?)\s*(minutes?|mins?|min|m)$/))) return Math.round(+m[1] * 60);
    if ((m = s.match(/^(\d{1,4})\s*(seconds?|secs?|sec|s)?$/))) return +m[1];
    return null;
  };
  const unitM = t.match(/(\d+(?:\.\d+)?)\s*(kg|lb)\b/);
  let body = unitM ? t.replace(unitM[0], ' ') : t;
  body = body.replace(/\b(\d+(?:\.\d+)?)\s*(minutes?|mins?|min|seconds?|secs?|sec|s|m)\b/g, '$1$2');
  let secs = [];
  const nx = body.match(/\b(\d{1,2})\s*x\s*(\d{1,2}:\d{2}|\d+(?:\.\d+)?(?:minutes?|mins?|min|seconds?|secs?|sec|s|m)?)\b/);
  if (nx) {
    const s = secsOf(nx[2]);
    if (s && +nx[1] >= 1 && +nx[1] <= 12) secs = Array(+nx[1]).fill(s);
  } else {
    const toks = body.match(/\b\d{1,2}:\d{2}\b|\b\d+(?:\.\d+)?(?:minutes?|mins?|min|seconds?|secs?|sec|s|m)\b|\b\d{1,3}\b/g) || [];
    secs = toks.map(secsOf).filter(Boolean);
  }
  secs = secs.filter(s => s > 0 && s <= 3600);
  if (!secs.length) return null;
  const w = unitM ? +unitM[1] : (/\bbw\b/.test(t) ? 0 : null);
  return { exercise: wordsOf(t), sets: secs.map(s => ({ w, reps: s, dur: s, rpe, unit: unitM ? unitM[2] : null })), unit: unitM ? unitM[2] : null, units, kind: 'time' };
}

function parseOne(text, { units = 'lb' } = {}) {
  let t = normalize(text);
  if (!/\d/.test(t) && !/\bbw\b/.test(t)) return null;

  // Bodyweight ± load: "bw+25", "bw - 40", "+25 x 8", "assisted ...".
  let load = null;
  t = t.replace(/\bbw\s*\+\s*(\d+(?:\.\d+)?)/, (m, n) => { load = 'weighted-bw'; return ` ${n} `; });
  t = t.replace(/\bbw\s*[-−–]\s*(\d+(?:\.\d+)?)/, (m, n) => { load = 'assisted'; return ` ${n} `; });
  if (!load) t = t.replace(/(^|\s)\+\s*(\d+(?:\.\d+)?)/, (m, a, n) => { load = 'weighted-bw'; return `${a}${n}`; });
  if (/\bassist(?:ed|ance)?\b/.test(t)) load = 'assisted';
  else if (!load && /\bbw\b/.test(t)) load = 'bodyweight';

  // Distance / duration ("5k run 25:30") and timed holds ("plank 60s").
  const cardio = parseCardio(t, units);
  if (cardio) return cardio;
  const rpe0 = (t.match(/(?:rpe\s*|@\s*rpe\s*)(\d+(?:\.\d)?)/) || [])[1];
  const hold = parseHold(t.replace(/(?:rpe\s*|@\s*rpe\s*)\d+(?:\.\d)?/, ' '), units, rpe0 && +rpe0 >= 5 && +rpe0 <= 10 ? +rpe0 : null);
  if (hold) return load ? { ...hold, load } : hold;

  const r = parseReps(t, units);
  return r && load ? { ...r, load } : r;
}

function parseReps(t, units) {
  // Negative numbers and absurd rep counts are typos, never "close enough".
  if (/(^|[\s,])-\s*\d/.test(t)) return null;
  if (/(?:x|for)\s*\d{4,}/.test(t) || /\b\d{4,}\s*reps?\b/.test(t)) return null;

  // RPE: "@8", "rpe 8", "at rpe 8.5"
  let rpe = null;
  const rpeM = t.match(/(?:rpe\s*|@\s*rpe\s*)(\d+(?:\.\d)?)/) || t.match(/@\s*(\d+(?:\.\d)?)\s*(?:rpe)?\s*$/);
  let body = t;
  if (rpeM) {
    const v = parseFloat(rpeM[1]);
    // "@ 185" is a weight, not an effort rating — only strip plausible RPEs.
    if (v >= 5 && v <= 10) { rpe = v; body = t.replace(rpeM[0], ' '); }
  }

  const unitM = body.match(/\b(kg|lb)\b/);
  const unit = unitM ? unitM[1] : null;
  const bw = /\bbw\b/.test(body);

  // Exercise words: everything before the first digit (minus filler words)
  const firstNum = body.search(/\d/);
  let exercise = (firstNum > 0 ? body.slice(0, firstNum) : '')
    .replace(/\b(i did|did|log|logged|add|set|sets|of|at|for|with|and|just|then|bw|kg|lb)\b/g, ' ')
    .replace(/[^a-z\s-]/g, ' ').replace(/\s+/g, ' ').trim();
  if (exercise.length < 3) exercise = null;
  body = firstNum > 0 ? body.slice(firstNum) : body;

  const num = '(\\d+(?:\\.\\d+)?)';
  let w = null, reps = [];
  let secsOnly = false;
  let m;

  // "5x5" / "3x10" alone (no unit, no load): sets x reps, load from the plan
  if (!unit && !bw && (m = body.match(/^\s*(\d{1,2})\s*x\s*(\d{1,2})\s*$/)) && +m[1] >= 1 && +m[1] <= 12 && +m[2] <= 12) {
    reps = Array(+m[1]).fill(+m[2]); w = null;
  }
  // "135 lb 10 reps", "100 kg 5 reps"
  else if ((m = body.match(new RegExp(`^\\s*${num}\\s*(?:kg|lb)\\s*(\\d{1,3})\\s*reps?\\b`)))) {
    w = +m[1]; reps = [+m[2]];
  }
  // "3x8 @ 185", "3 x 8 at 185", "3 sets of 8 at 185"
  else if ((m = body.match(new RegExp(`^\\s*(\\d{1,2})\\s*(?:x|sets?\\s*(?:of|x)?)\\s*(\\d{1,3})\\s*(?:reps?)?\\s*(?:@|at|with)\\s*${num}`)))) {
    const n = +m[1], r = +m[2]; w = +m[3];
    if (n >= 1 && n <= 12) reps = Array(n).fill(r);
  }
  // "185 x 8, 8, 7" / "185x8x3"? (weight x reps [x sets])
  else if ((m = body.match(new RegExp(`^\\s*${num}\\s*(?:kg|lb)?\\s*(?:x|for|reps?)\\s*(\\d{1,3})((?:\\s*(?:,|and)?\\s*\\d{1,3}\\b)*)`)))) {
    w = +m[1];
    reps = [+m[2]];
    const rest = (m[3] || '').match(/\d{1,3}/g) || [];
    // "185x8x3" means 3 sets of 8
    const xSets = body.match(new RegExp(`^\\s*${num}\\s*(?:kg|lb)?\\s*x\\s*(\\d{1,3})\\s*x\\s*(\\d{1,2})\\b`));
    if (xSets) reps = Array(Math.min(12, +xSets[3])).fill(+xSets[2]);
    else reps.push(...rest.map(Number));
  }
  // "8 reps at 185" / "8 at 185"
  else if ((m = body.match(new RegExp(`^\\s*(\\d{1,3})\\s*(?:reps?)?\\s*(?:@|at|with)\\s*${num}`)))) {
    reps = [+m[1]]; w = +m[2];
  }
  // "12 reps" (bodyweight) / "60 seconds"
  else if ((m = body.match(/^\s*(\d{1,3})\s*(reps?|seconds?|secs?|s)\b/))) {
    reps = [+m[1]]; w = bw ? 0 : null;
    if (!/^rep/.test(m[2])) secsOnly = true;
  }
  // bare "bw 12"
  else if (bw && (m = body.match(/(\d{1,3})/))) {
    reps = [+m[1]]; w = 0;
  }

  reps = reps.filter(r => r > 0 && r <= 100);
  if (!reps.length) return null;
  if (w != null && (w < 0 || w > 2000)) return null;
  if (bw) w = 0;
  if (secsOnly) return { exercise, sets: reps.map(r => ({ w, reps: r, dur: r, rpe, unit: unit || null })), unit: unit || null, units, kind: 'time' };
  return { exercise, sets: reps.map(r => ({ w, reps: r, rpe, unit: unit || null })), unit: unit || null, units, kind: 'reps' };
}

// Converts a parsed weight in display/explicit units to stored lb. A set's own
// unit wins ("60kg x 5, 135 x 5" in an lb profile → 132.3 lb, 135 lb).
export function toLb(w, parsed, set) {
  if (w == null) return null;
  const u = (set && set.unit) || (set && 'unit' in set ? parsed.units : (parsed.unit || parsed.units));
  return u === 'kg' ? +(w / 0.45359237).toFixed(4) : w;
}
